import IconCreditCard from "@/assets/icons/credit-card.svg?react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { Minus, Star } from "lucide-react";
import {
  formatCardExpiry,
  formatCardLabel,
  type BankCard,
} from "../api.ts";

interface Props {
  card: BankCard;
  onRemove?: (id: string) => void;
  onSetDefault?: (id: string) => void;
  showRemove?: boolean;
  disabled?: boolean;
}

// DON list-card pattern: rounded-xl + bg-gray-4 + border-white/[0.04].
// Default karta yulduzcha bilan belgilanadi; delete-mode'da chap tomondan
// qizil "−" tugmasi paydo bo'ladi (iOS edit-style).
export default function BankCardItem({
  card,
  onRemove,
  onSetDefault,
  showRemove,
  disabled,
}: Props) {
  const expiry = formatCardExpiry(card);
  return (
    <div className="w-full flex items-center">
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          showRemove
            ? "w-9 mr-2 opacity-100 scale-100"
            : "w-0 mr-0 opacity-0 scale-50"
        }`}
      >
        <VibratingButton
          onClick={() => onRemove?.(card.id)}
          vibrationType="light"
          className="size-6 rounded-full bg-red flex-center shrink-0 active:scale-90 transition-transform"
          aria-label="Remove card"
          disabled={!showRemove || disabled}
        >
          <Minus className="size-5 text-white" strokeWidth={3} />
        </VibratingButton>
      </div>

      <div
        className={`flex-1 min-w-0 rounded-xl bg-gray-4 border border-white/08 p-3 flex items-center gap-3 transition-transform duration-300 ease-out origin-right ${
          showRemove ? "scale-x-[0.99]" : "scale-x-100"
        }`}
      >
        <div className="size-11 rounded-lg bg-orange-100/15 flex-center shrink-0">
          <IconCreditCard className="size-6 text-orange-100" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-130 truncate">
            {formatCardLabel(card)}
          </p>
          <p className="text-xs text-gray-1 leading-130 mt-0.5 truncate">
            {card.owner ? `${card.owner}` : ""}
            {card.owner && expiry ? " · " : ""}
            {expiry}
          </p>
        </div>

        {card.is_default ? (
          <div className="size-6 rounded-full bg-orange-100/15 flex-center shrink-0">
            <Star className="size-4 text-orange-100" fill="currentColor" />
          </div>
        ) : onSetDefault ? (
          <VibratingButton
            onClick={() => onSetDefault(card.id)}
            vibrationType="selection"
            className="size-6 rounded-full border border-white/08 flex-center shrink-0 hover:bg-white/[0.04] transition-colors"
            aria-label="Set default"
            disabled={disabled}
          >
            <Star className="size-3.5 text-gray-1" />
          </VibratingButton>
        ) : null}
      </div>
    </div>
  );
}
